import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "../components/Header";
import { retornoTokenObter, retornoTokenEnviar } from "../services/api";

// 📨 A EMPRESA SEM CONTA RESPONDE O CANDIDATO (03/11/2026, Nelson: "precisamos cobrar todas, indiferentemente").
//
// Chega por /retorno/{token}, o link de cada candidato no resumo semanal de cobrança. Quem recebe esse resumo não
// tem login no eaiRH (o contato veio da vaga, da prospecção ou de outro candidato), então o painel não serve: aqui
// a pessoa escreve o retorno sem conta nenhuma, e ele vai para o candidato e conta na reputação da empresa. Uma
// resposta por e-mail comum não faria nenhuma das duas coisas.
//
// Sem moderação, ao contrário do /responder/{token} do relato: aqui não há texto público, é só uma mensagem para
// quem está esperando.
const MINIMO = 10;

export default function RetornoPage() {
  const { token } = useParams();
  const [estado, setEstado] = useState("carregando"); // carregando | ok | invalido | respondido | enviado
  const [info, setInfo] = useState(null);
  const [texto, setTexto] = useState("");
  const [autor, setAutor] = useState("");
  const [busy, setBusy] = useState(false);
  const [erro, setErro] = useState("");

  // Link de uso privado (mostra o nome do candidato): nunca no índice. O servidor também manda X-Robots-Tag.
  useEffect(() => {
    document.title = "Dar retorno ao candidato · eaiRH";
    const m = document.createElement("meta"); m.name = "robots"; m.content = "noindex, nofollow";
    document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);

  useEffect(() => {
    let vivo = true;
    retornoTokenObter(token)
      .then((r) => {
        if (!vivo) return;
        setInfo(r);
        setEstado(r.jaRespondido ? "respondido" : "ok");
      })
      .catch(() => { if (vivo) setEstado("invalido"); });
    return () => { vivo = false; };
  }, [token]);

  const primeiroNome = (info?.candidato || "").trim().split(/\s+/)[0] || "o candidato";
  const dias = info?.diasEsperando ?? 0;

  const enviar = async () => {
    if (busy) return;
    if (texto.trim().length < MINIMO) { setErro("Escreva ao menos uma frase para a pessoa."); return; }
    setBusy(true); setErro("");
    try {
      await retornoTokenEnviar(token, texto.trim(), autor.trim());
      setEstado("enviado");
    } catch (e) {
      setErro(e.message || "Não foi possível enviar. Tente de novo.");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-xl mx-auto px-4 py-10">
        {estado === "carregando" && <p className="text-gray-500">Carregando…</p>}

        {estado === "invalido" && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
            <div className="text-4xl">⚠️</div>
            <h1 className="mt-2 text-xl font-bold text-red-700">Link inválido</h1>
            {/* Quem chega aqui decidiu responder: não pode bater numa parede sem saída. */}
            <p className="mt-1 text-sm text-gray-600">
              Não encontramos esta candidatura. Confira se o endereço foi copiado inteiro ou peça um link novo:
              respondemos no mesmo dia.
            </p>
            <a href="mailto:contato@eairh.com.br?subject=Preciso%20de%20um%20novo%20link%20de%20retorno&body=Ol%C3%A1!%20O%20link%20para%20dar%20retorno%20ao%20candidato%20n%C3%A3o%20abriu.%20Podem%20me%20enviar%20um%20novo%3F%0A%0AEmpresa%3A%20%0AVaga%3A%20"
              className="inline-block mt-4 rounded-xl bg-primary hover:bg-primary/90 text-white text-sm font-semibold px-5 py-2.5">
              Pedir um link novo
            </a>
          </div>
        )}

        {estado === "respondido" && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
            <div className="text-4xl">✓</div>
            <h1 className="mt-2 text-xl font-bold text-green-700">Retorno já registrado</h1>
            <p className="mt-1 text-sm text-gray-600">
              {primeiroNome === "o candidato" ? "O candidato" : primeiroNome} já recebeu um retorno sobre a vaga{" "}
              <b>{info?.vaga}</b>. Obrigado por responder!
            </p>
            <Link to="/" className="inline-block mt-4 text-sm text-primary font-semibold hover:underline">Conhecer o eaiRH →</Link>
          </div>
        )}

        {estado === "enviado" && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm text-center">
            <div className="text-4xl">🙌</div>
            <h1 className="mt-2 text-xl font-bold text-primary">Retorno enviado!</h1>
            <p className="mt-2 text-sm text-gray-600">
              Obrigado. Avisamos {primeiroNome === "o candidato" ? "o candidato" : primeiroNome} por e-mail, e o retorno já conta
              na página da {info?.empresa || "empresa"} no eaiRH. Se houver outros candidatos esperando, cada um tem o próprio
              link no e-mail que você recebeu.
            </p>
            <p className="mt-2 text-xs text-gray-400">O eaiRH é gratuito para empresas. Só pedimos respeito e retorno aos candidatos. 💙</p>
            <Link to="/" className="inline-block mt-4 text-sm text-primary font-semibold hover:underline">Conhecer o eaiRH →</Link>
          </div>
        )}

        {estado === "ok" && info && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold text-primary uppercase tracking-wide">Retorno ao candidato · eaiRH</p>
            <h1 className="mt-1 text-xl font-bold text-gray-900">Responder como {info.empresa}</h1>
            <p className="mt-1 text-sm text-gray-500">
              Esta pessoa se candidatou e ainda espera uma resposta. Escreva o retorno aqui, sem login e sem criar conta:
              ele vai direto para ela.
            </p>

            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-1">
              <p className="text-sm"><span className="text-gray-500">Vaga:</span> <b className="text-gray-800">{info.vaga}</b></p>
              {info.candidato && (
                <p className="text-sm"><span className="text-gray-500">Candidato:</span> <b className="text-gray-800">{info.candidato}</b></p>
              )}
              <p className="text-sm">
                <span className="text-gray-500">Esperando há:</span>{" "}
                <b className="text-amber-700">{dias} dia{dias === 1 ? "" : "s"}</b>
                {info.candidatouEm && (
                  <span className="text-gray-400"> (candidatura em {new Date(info.candidatouEm).toLocaleDateString("pt-BR")})</span>
                )}
              </p>
              {info.etapaQueOCandidatoDeclarou && (
                <p className="text-xs text-gray-500">Etapa que a pessoa informou: {info.etapaQueOCandidatoDeclarou}</p>
              )}
              <p className="text-[10px] text-blue-700/80 pt-1">O nome aparece só aqui, para vocês localizarem o processo. Não é público.</p>
            </div>

            <div className="mt-4">
              <label className="block text-xs font-semibold text-gray-600 mb-1">Seu retorno para {primeiroNome}</label>
              <textarea
                value={texto} onChange={(e) => setTexto(e.target.value)} rows={6} maxLength={4000}
                placeholder={`Ex.: Olá, ${primeiroNome === "o candidato" ? "tudo bem" : primeiroNome}! Obrigado pelo interesse na vaga. Neste momento seguimos com outro perfil, mas guardamos seu currículo para as próximas oportunidades.`}
                className="w-full rounded-lg border border-gray-300 p-3 text-sm" />
              <p className="mt-1 text-[11px] text-gray-400">
                {texto.trim().length < MINIMO
                  ? `Mínimo de ${MINIMO} caracteres. Uma frase já basta.`
                  : "Pronto para enviar."}
              </p>
              <div className="mt-2">
                <label className="block text-xs font-semibold text-gray-600 mb-1">Seu nome e cargo <span className="font-normal text-gray-400">(opcional)</span></label>
                <input value={autor} onChange={(e) => setAutor(e.target.value)} maxLength={200}
                  placeholder="Ex.: Ricardo, Recrutamento e Seleção"
                  className="w-full rounded-lg border border-gray-300 p-2 text-sm" />
              </div>
              {erro && <p className="mt-2 text-xs text-red-600">{erro}</p>}
              <button onClick={enviar} disabled={busy || texto.trim().length < MINIMO}
                className="mt-3 w-full rounded-lg bg-primary text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-50">
                {busy ? "Enviando…" : "Enviar retorno"}
              </button>
              <p className="mt-2 text-[11px] text-gray-400 text-center">
                Sem criar conta. O eaiRH é gratuito para empresas: só pedimos respeito e retorno aos candidatos.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
